/* eslint-disable */
import * as React from "react";
import { WithStyles, withStyles } from "@material-ui/styles";
import { Box, Typography, TextField, Button } from "@material-ui/core";
import { FormattedMessage } from "react-intl";
import { isIE } from "react-device-detect";

import { useDepartments, Department } from "../../../modules/dictionaries/shared";
import { ComboBoxField } from "../../../components/ComboBoxField";
import messages from "../constants/messages";
import {
  INITIAL_FORM_STATE,
  addUserFormConfig,
  namePatt,
  loginPatt,
  MAX_LOGIN_LENGTH,
  MAX_NAME_LENGTH,
  MAX_SURNAME_LENGTH,
} from "../constants/constants";
import { styles } from "../constants/styles";
import { UserInputFormProps } from "../constants/types";
import { useAdministration, useAdministrationError, AdministrationError } from "../../../modules/admin";
import cn from "classnames";
import { useEffect } from "react";
import { AVAILEBLE_EMAIL_DOMAINS } from "../../../modules/auth/pages/RegisterPage/constants/constants";

type Props = UserInputFormProps & WithStyles<typeof styles>;

const UserInputForm: React.FC<Props> = (props: Props) => {
  const [, , , createUser, updateUser] = useAdministration();
  const { classes, row, variant } = props;
  const [administrationError] = useAdministrationError();
  const [, departmentsWithNamesDeleted] = useDepartments();

  let initialState = INITIAL_FORM_STATE;
  if (row && variant === "edit") {
    const { departmentId, name, surname, email } = row;
    initialState = {
      ...INITIAL_FORM_STATE,
      department: departmentId,
      requiredFields: {
        name,
        surname,
        login: email,
      },
    };
  }
  if (variant === "add") {
    const department = departmentsWithNamesDeleted.find((item) => item.isDeleted === false);
    initialState = {
      ...INITIAL_FORM_STATE,
      department: department ? department.id : INITIAL_FORM_STATE.department,
    };
  }
  const [formState, setFormState] = React.useState(initialState);

  useEffect(() => {
    if (administrationError === AdministrationError.DuplicateEmail) {
      setFormState({ ...formState, errors: { ...formState.errors, loginResponse: true }, showErrorMessage: true });
    }
  }, [administrationError]);

  useEffect(() => {
    setFormState({ ...formState, showErrorMessage: false });
  }, [variant]);

  const getErrors = (id: string, value: string) => {
    let errors = {};
    if (id === "login") {
      let loginError = !value;
      if (!loginError) {
        const login = value.split("@")[0] || "";
        loginError = !login.match(loginPatt);
      }
      errors[id] = loginError;

      const domain = value.split("@")[1] || "";
      const domainError = !AVAILEBLE_EMAIL_DOMAINS.some((d) => d === domain);
      errors["domain"] = domainError;
    }
    if (id === "name" || id === "surname") {
      const nameOrSurnameError = !value || !value.match(namePatt);
      errors[id] = nameOrSurnameError;
    }
    if (id === "department") {
      errors[id] = !value;
    }

    return errors;
  };

  const handleChangeText = (id: string) => (event: any) => {
    let errors = {};

    if (formState.showErrorMessage) {
      errors = getErrors(id, event.target.value.trim());
    }

    if (Object.keys(formState.requiredFields).includes(id)) {
      setFormState({
        ...formState,
        requiredFields: { ...formState.requiredFields, [id]: event.target.value.trim() },
        dirty: true,
        errors: {
          ...formState.errors,
          ...errors,
          loginResponse: id === "login" ? false : formState.errors.loginResponse,
        },
      });
    } else {
      setFormState({
        ...formState,
        [id]: event.target.value,
        dirty: true,
        errors: {
          ...formState.errors,
          ...errors,
        },
      });
    }
  };

  const handleChangeDepartment = (event: React.ChangeEvent<{}>, value: any) => {
    if (value) {
      setFormState({
        ...formState,
        department: value.id,
        errors: { ...formState.errors, department: false },
        dirty: true,
      });
    } else {
      setFormState({ ...formState, department: 0, errors: { ...formState.errors, department: true }, dirty: true });
    }
  };

  const handleApply = () => {
    const isValid = checkValidity();

    if (isValid) {
      const { department } = formState;
      const { name, surname, login } = formState.requiredFields;
      const { handleCancel } = props;

      if (variant === "edit") {
        const { id, registrationDate, profilePicture } = props.row!;
        updateUser({
          id,
          name,
          surname,
          email: login,
          departmentId: department,
          registrationDate,
          profilePicture,
        });
        handleCancel();
      } else if (variant === "add") {
        const result = createUser({
          name,
          surname,
          email: login,
          departmentId: department,
        });

        result.then((res) => {
          if (res) {
            handleCancel();
          }
        });
      }
    }
  };

  const checkValidity = () => {
    const { name, surname, login } = formState.requiredFields;
    const nameError = !name.length || !name.match(namePatt) || name.length > MAX_NAME_LENGTH;
    const surnameError = !surname.length || !surname.match(namePatt) || surname.length > MAX_SURNAME_LENGTH;
    let loginError = !login;
    if (!loginError) {
      const leftPieceEmail = login.split("@")[0] || "";
      loginError = !leftPieceEmail.match(loginPatt) || leftPieceEmail.length > MAX_LOGIN_LENGTH;
    }
    const domain = login.split("@")[1] || "";
    const domainError = !AVAILEBLE_EMAIL_DOMAINS.some((d) => d === domain);
    const departmentError = !formState.department;
    const errors = [nameError, surnameError, loginError, domainError, departmentError];
    const showErrorMessage = errors.some((error) => error === true);

    setFormState({
      ...formState,
      errors: {
        ...formState.errors,
        login: loginError,
        name: nameError,
        surname: surnameError,
        domain: domainError,
        department: departmentError,
      },
      showErrorMessage,
    });

    return errors.every((error) => error === false);
  };

  const renderHelperText = (id: string) => {
    if (id === "name" || id === "surname") {
      return (
        (!formState.requiredFields[id].length && formState.showErrorMessage && (
          <FormattedMessage {...messages.fieldIsRequired} />
        )) ||
        (formState.showErrorMessage && formState.errors[id] && <FormattedMessage {...messages.checkCorrectness} />)
      );
    }

    if (id === "department" && !formState.department) {
      return <FormattedMessage {...messages.fieldIsRequired} />;
    }

    if (id === "login") {
      return (
        formState.showErrorMessage &&
        ((!formState.requiredFields.login.length && <FormattedMessage {...messages.fieldIsRequired} />) ||
          (!formState.errors.login && formState.errors.domain && isEmptyDomain() && (
            <FormattedMessage {...messages.checkCorrectness} />
          )) ||
          (!formState.errors.login && formState.errors.domain && <FormattedMessage {...messages.emailNotInSystem} />) ||
          (formState.errors.loginResponse && !formState.errors.login && !formState.errors.domain && (
            <FormattedMessage {...messages.responseErrorAdminDuplicateEmail} />
          )))
      );
    }

    return undefined;
  };

  const isEmptyDomain = () => {
    if (formState.requiredFields.login.length) {
      const domain = formState.requiredFields.login.split("@")[1] || "";
      return domain ? false : true;
    }
    return true;
  };

  const headerTitleMessage = variant === "add" ? messages.addUserFormHeader : messages.editUserFormHeader;

  return (
    <Box className={classes.modalBox}>
      <Typography className={classes.modalHeader}>
        <FormattedMessage {...headerTitleMessage} />
      </Typography>
      {addUserFormConfig.map((field) => {
        return (
          <div className={classes.fieldItem} key={field.id}>
            {field.type === "text" && (
              <TextField
                id={field.id}
                className={classes.fieldContent}
                error={
                  field.id === "login"
                    ? formState.errors.login || formState.errors.domain || formState.errors.loginResponse
                    : formState.errors[field.id]
                }
                type={"text"}
                variant="outlined"
                fullWidth
                value={formState.requiredFields[field.id]}
                label={<FormattedMessage {...field.name} />}
                InputLabelProps={{ classes: { root: classes.modalInputLabel }, shrink: isIE ? true : undefined }}
                onChange={handleChangeText(field.id)}
                helperText={renderHelperText(field.id)}
                FormHelperTextProps={{ classes: { root: classes.helperText } }}
                classes={{ root: classes.textField }}
                inputProps={{ maxLength: field.maxLength }}
              ></TextField>
            )}
            {field.type === "select" && (
              <ComboBoxField
                key={
                  departmentsWithNamesDeleted && departmentsWithNamesDeleted[0]
                    ? departmentsWithNamesDeleted[0].id
                    : "nodata"
                }
                options={departmentsWithNamesDeleted}
                getOptionLabel={(option) => option.name}
                inputLabel={messages.department}
                defaultValue={
                  variant === "add"
                    ? departmentsWithNamesDeleted.find((item) => item.isDeleted === false)
                    : departmentsWithNamesDeleted.find((item) => item.id === row.departmentId)
                }
                filterOptions={(options: Department[], state: any) => {
                  return options.filter(
                    (item) =>
                      item.isDeleted === false &&
                      (!!state.inputValue.length
                        ? item.name.toLowerCase().includes(state.inputValue.toLowerCase())
                        : true),
                  );
                }}
                onChange={handleChangeDepartment}
                inputLabelClassName={classes.modalInputLabel}
                className={classes.comboBox}
                error={formState.errors.department}
                helperText={renderHelperText("department") || undefined}
              />
            )}
          </div>
        );
      })}
      <div className={classes.modalButtonBase}>
        <Button
          className={classes.modalButtons}
          variant="contained"
          color="primary"
          onClick={handleApply}
          disabled={row && variant === "edit" && !formState.dirty}
        >
          <FormattedMessage {...messages.saveButton} />
        </Button>
        <Button
          className={cn(classes.modalButtons, classes.btnCancel)}
          variant="contained"
          onClick={props.handleCancel}
        >
          <FormattedMessage {...messages.cancelButton} />
        </Button>
      </div>
    </Box>
  );
};

export default withStyles(styles)(UserInputForm);
